import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { apiService } from '../services/api';
import './Registration.css';

const detectDeviceOS = () => {
  const ua = (navigator.userAgent || '').toLowerCase();
  if (ua.includes('iphone') || ua.includes('ipad') || ua.includes('ipod')) return 'iOS';
  if (ua.includes('android')) return 'Android';
  return 'Android'; // Default preferred mobile OS
};

const ANDROID_VERSIONS = [
  { val: 'Android 17', label: 'Android 17' },
  { val: 'Android 16', label: 'Android 16' },
  { val: 'Android 15', label: 'Android 15' },
  { val: 'Android 14', label: 'Android 14' },
  { val: 'Android 13', label: 'Android 13' },
  { val: 'Android 12', label: 'Android 12' },
  { val: 'Android 11', label: 'Android 11' },
  { val: 'Android 10', label: 'Android 10' },
  { val: 'Android 9', label: 'Android 9 (Pie)' },
  { val: 'Other', label: 'Other / Custom Version' }
];

const IOS_VERSIONS = [
  { val: 'iOS 18', label: 'iOS 18 (Latest)' },
  { val: 'iOS 17', label: 'iOS 17' },
  { val: 'iOS 16', label: 'iOS 16' },
  { val: 'iOS 15', label: 'iOS 15' },
  { val: 'iOS 14', label: 'iOS 14' },
  { val: 'Other', label: 'Other / Custom Version' }
];

const Registration = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile_no: '',
    dob: '',
    os: detectDeviceOS(),
    os_version: detectDeviceOS() === 'iOS' ? 'iOS 17' : 'Android 14',
    custom_version: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [consentAccepted, setConsentAccepted] = useState(false);

  // Auto-detect referral from URL params (e.g., ?ref=invite123)
  const queryParams = new URLSearchParams(location.search);
  const referralFromUrl = queryParams.get('ref') || '';

  // Redirect if already logged in
  useEffect(() => {
    const userId = sessionStorage.getItem('userId');
    const userName = sessionStorage.getItem('userName');
    if (userId && userName) {
      navigate(`/dashboard/${encodeURIComponent(userName)}`);
    }
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'os') {
      // When OS changes, set default version for that OS
      const defaultVersion = value === 'iOS' ? 'iOS 17' : (value === 'Android' ? 'Android 14' : '');
      setFormData({
        ...formData,
        os: value,
        os_version: defaultVersion,
        custom_version: ''
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const validateForm = () => {
    const { name, email, mobile_no, dob, os, os_version, custom_version } = formData;

    if (!name.trim()) {
      setError('Full Name is required');
      return false;
    }

    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
      setError('A valid email address is required');
      return false;
    }

    if (!mobile_no.trim() || !/^\d{10,15}$/.test(mobile_no.replace(/\D/g, ''))) {
      setError('Valid mobile number is required (10-15 digits)');
      return false;
    }

    if (!dob) {
      setError('Date of birth is required');
      return false;
    }

    if (!os) {
      setError('Please select your Operating System (Android or iOS)');
      return false;
    }

    if (!os_version) {
      setError(`Please select your ${os} version`);
      return false;
    }

    if (os_version === 'Other' && !custom_version.trim()) {
      setError('Please enter your specific OS version');
      return false;
    }

    if (!consentAccepted) {
      setError('You must accept the consent agreement to proceed');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const finalVersion = formData.os_version === 'Other'
        ? `${formData.os} ${formData.custom_version.trim()}`
        : formData.os_version;

      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        mobile_no: formData.mobile_no.trim(),
        dob: formData.dob,
        android_version: finalVersion,
        device_os: formData.os,
        country: 'IN',
        gender: 'Male',
        ethnicity: 'south_asian',
        referral_by: referralFromUrl
      };

      const response = await apiService.registerUser(payload);
      const userId = response.user_id;
      const userName = response.name;
      sessionStorage.setItem('userId', String(userId));
      sessionStorage.setItem('userName', userName);
      navigate(`/dashboard/${encodeURIComponent(userName)}`);
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.response?.data?.error || 'Registration failed. Please check your details and try again.');
    } finally {
      setLoading(false);
    }
  };

  const currentVersionList = formData.os === 'iOS' ? IOS_VERSIONS : ANDROID_VERSIONS;

  return (
    <div className="registration-container">
      <h2>User Registration</h2>
      <form onSubmit={handleSubmit} className="registration-form">
        {/* 1. Full Name */}
        <div className="form-group">
          <label htmlFor="name">Full Name:</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter your full name"
            autoComplete="name"
            required
          />
        </div>

        {/* 2. Email */}
        <div className="form-group">
          <label htmlFor="email">Email:</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Enter your email address"
            autoComplete="email"
            required
          />
        </div>

        {/* 3. Mobile Number */}
        <div className="form-group">
          <label htmlFor="mobile_no">Mobile Number:</label>
          <input
            type="tel"
            id="mobile_no"
            name="mobile_no"
            value={formData.mobile_no}
            onChange={handleChange}
            placeholder="Enter your mobile number"
            autoComplete="tel"
            required
          />
        </div>

        {/* 4. Date of Birth */}
        <div className="form-group">
          <label htmlFor="dob">Date of Birth:</label>
          <input
            type="date"
            id="dob"
            name="dob"
            value={formData.dob}
            onChange={handleChange}
            required
          />
        </div>

        {/* 5. Operating System (Dropdown: Android / iOS) */}
        <div className="form-group">
          <label htmlFor="os">Operating System:</label>
          <select
            id="os"
            name="os"
            value={formData.os}
            onChange={handleChange}
            required
          >
            <option value="">-- Select Operating System --</option>
            <option value="Android">Android</option>
            <option value="iOS">iOS</option>
          </select>
        </div>

        {/* 6. OS Version (Dependent Dropdown & Input) */}
        {formData.os && (
          <div className="form-group">
            <label htmlFor="os_version">
              {formData.os} Version:
            </label>
            <select
              id="os_version"
              name="os_version"
              value={formData.os_version}
              onChange={handleChange}
              required
            >
              <option value="">-- Select {formData.os} Version --</option>
              {currentVersionList.map((ver) => (
                <option key={ver.val} value={ver.val}>
                  {ver.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* 6b. Custom Version Text Field (if "Other" is chosen) */}
        {formData.os_version === 'Other' && (
          <div className="form-group">
            <label htmlFor="custom_version">Specific {formData.os} Version:</label>
            <input
              type="text"
              id="custom_version"
              name="custom_version"
              value={formData.custom_version}
              onChange={handleChange}
              placeholder="e.g. 14.1.2"
              required
            />
          </div>
        )}

        {/* 7. Consent Agreement */}
        <div className="form-group consent-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={consentAccepted}
              onChange={(e) => setConsentAccepted(e.target.checked)}
              required
            />
            <span>
              I agree to the <Link to="/consent" target="_blank" rel="noopener noreferrer">Consent Agreement</Link> and permit processing of my photos.
            </span>
          </label>
        </div>

        {error && <div className="error-message">{error}</div>}

        <button type="submit" className="submit-btn" disabled={loading}>
          {loading ? 'Registering...' : 'Register & Continue'}
        </button>
      </form>
    </div>
  );
};

export default Registration;
